import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Button,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import mainStyles from "styles";

import { FeatureFlagSegment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/feature_flag_pb";

import OrgSegmentRecord from "./components/org-segment-record";
import { listFeatureFlagSegments } from "api/endpoints/feature-flag-segments";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface OrgSegmentTabProps {
  regionCode: string;
  roleId: string;
  refreshOrgData: () => void;
}
const OrgSegmentsTab = ({
  regionCode,
  roleId,
  refreshOrgData,
}: OrgSegmentTabProps) => {
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(false);

  const [segmentList, setSegmentList] = useState<FeatureFlagSegment[]>([]);

  const [isEditableFilterOn, setIsEditableFilterOn] = useState(true);
  const [isSearchingFlagName, setIsSearchingFlagName] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const clearSearch = () => {
    setSearchVal("");
  };
  const onSuccess = useCallback(() => {
    setIsLoading(true);
    listFeatureFlagSegments(
      {
        organisationIds: [toUUID(roleId)],
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        const segmentsListOutput = res.segments;
        setSegmentList(segmentsListOutput);
      })
      .then(refreshOrgData)
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [regionCode, roleId, showError]);

  useEffect(() => {
    onSuccess();
  }, [onSuccess]);

  const populateOrgSegmentData = (segmentsListValues: FeatureFlagSegment[]) => {
    let filteredSegmentListValues = segmentsListValues;
    if (isEditableFilterOn) {
      filteredSegmentListValues = segmentsListValues.filter(
        (segment) => segment.editable
      );
    }

    let searchFilteredListValues = filteredSegmentListValues;
    if (isSearchingFlagName) {
      searchFilteredListValues = filteredSegmentListValues.filter((segment) =>
        segment.flags.some((flag) =>
          flag.name.toLowerCase().includes(searchVal.toLowerCase())
        )
      );
    } else {
      searchFilteredListValues = filteredSegmentListValues.filter((segment) =>
        segment.name.toLowerCase().includes(searchVal.toLowerCase())
      );
    }

    if (segmentsListValues.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            <b>{"Failed to fetch segment data from the featureflags API."}</b>
          </TableCell>
        </TableRow>
      );
    }

    return searchFilteredListValues.map((segmentListInfo, index) => {
      return (
        <OrgSegmentRecord
          key={index}
          segmentListInfo={segmentListInfo}
          regionCode={regionCode}
          roleId={roleId}
          onSuccess={onSuccess}
        />
      );
    });
  };

  return (
    <>
      <Grid container sx={classes.paddingTopSmall}>
        <TableContainer component={Paper}>
          <Table aria-label="segment-list-table">
            <TableHead>
              <TableRow>
                <TableCell
                  colSpan={5}
                  style={{ paddingLeft: "30px", paddingRight: "30px" }}
                >
                  <Grid container direction="row" spacing={3}>
                    <Grid
                      item
                      xs
                      container
                      spacing={2}
                      justifyContent="flex-start"
                    >
                      <Grid item xs>
                        <TextField
                          variant="outlined"
                          label="Segment/Feature Flag search"
                          size="small"
                          fullWidth
                          inputProps={{
                            "data-testid": "org-segment-search-bar",
                          }}
                          value={searchVal}
                          onChange={(e) => setSearchVal(e.target.value)}
                          disabled={isLoading}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={clearSearch}
                          data-testid="org-segment-search-bar-clear-button"
                        >
                          Clear
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <ToggleButtonGroup
                        value={isSearchingFlagName}
                        exclusive
                        size="small"
                        onChange={(event, value) => {
                          setIsSearchingFlagName(value);
                        }}
                        aria-label="search toggle switch"
                        color="primary"
                        disabled={isLoading}
                      >
                        <ToggleButton
                          value={false}
                          color="primary"
                          aria-label="feature flag"
                          sx={classes.toogleButtonColor}
                        >
                          Segment name
                        </ToggleButton>
                        <ToggleButton
                          value={true}
                          color="primary"
                          aria-label="feature flag"
                          sx={classes.toogleButtonColor}
                        >
                          Feature flag
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs container justifyContent="flex-end">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isEditableFilterOn}
                            onChange={() =>
                              setIsEditableFilterOn(!isEditableFilterOn)
                            }
                            name="checkedB"
                            color="primary"
                            data-testid="is-editable-only-toggle-switch"
                            disabled={isLoading}
                          />
                        }
                        label="Editable Segments Only"
                      />
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ width: "25%", paddingLeft: "30px" }}>
                  Segment Name
                </TableCell>
                <TableCell style={{ width: "30%" }}>Description</TableCell>
                <TableCell style={{ width: "15%" }}>Feature Flags</TableCell>
                <TableCell style={{ width: "15%" }}>Tags</TableCell>
                <TableCell style={{ width: "15%" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                populateOrgSegmentData(segmentList)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </>
  );
};

export default OrgSegmentsTab;
